import { useMemo, useRef, type FC } from 'react';

import dynamic from 'next/dynamic';

import Grid2 from '@mui/material/Unstable_Grid2';

import Box from '@mui/material/Box';

import { nanoid } from 'nanoid';

import CustomAvatar from '@/@core/components/mui/Avatar';
import TextAction from '@/components/text-action/TextAction';
import { DisplayField } from '@/components/typography/DisplayField';
import { DEFAULT_DISPLAY } from '@/constants/default-value/default-value';
import { useAverageRating } from '@/hooks/apps/dashboard';
import { useOverViewUserListTaskChecks } from '@/hooks/apps/overview-user-list-task-check';
import { useEntityImageStore } from '@/hooks/useEntityImage';
import { EntityNameEnum } from '@/types/enums/entity-name.enum';
import { getNumberInitials } from '@/utils/getNumberInitials';
import { t } from '@/utils/lang';
import { routeManager } from '@/utils/route';

import EntityDetailButton from '../../Button';
import EntityDetailContainer from '../../Container/EntityDetailContainer';

const BusinessTripFormTable = dynamic(
    () => import('./BussinessTripFormTable'),
    {
        ssr: false,
    }
);

const entityDetailIdentifier = `${routeManager.STAFF}/${nanoid()}`;

const AssigneeInfo: FC<{ data: any }> = ({ data: detail }) => {
    const info = useMemo(
        () => [
            {
                id: 1,
                title: (
                    <TextAction value={detail?.position} copy>
                        {t('Vị trí')}
                    </TextAction>
                ),
                value: detail?.position,
            },
            {
                id: 2,
                title: (
                    <TextAction value={detail?.username} copy>
                        {t('Tài khoản')}
                    </TextAction>
                ),
                value: detail?.username,
            },
            {
                id: 3,
                title: (
                    <TextAction value={detail?.email} copy>
                        {t('Email')}
                    </TextAction>
                ),
                value: detail?.email,
            },
            {
                id: 4,
                title: (
                    <TextAction value={detail?.phoneNumber} copy>
                        {t('Số điện thoại')}
                    </TextAction>
                ),
                value: detail?.phoneNumber,
            },
            {
                id: 5,
                title: t('Vai trò'),
                value: detail?.appRoles
                    .map((item: any) => item.name)
                    .join(', '),
            },
        ],
        [detail]
    );

    const { url } = useEntityImageStore(EntityNameEnum.AppUser, detail?.id);

    const { data: task } = useOverViewUserListTaskChecks({
        AssigneeId: detail.id,
    });

    const { data: averageRating } = useAverageRating({ userId: detail.id });
    const avg = averageRating?.result?.averageRating;

    const modalRatingRef = useRef<{ open: () => void }>(null);

    return (
        <EntityDetailContainer identifier={entityDetailIdentifier}>
            <Grid2 container spacing={5} alignItems='flex-start' sx={{ mb: 5 }}>
                <Grid2 xs='auto'>
                    <Box>
                        {url ? (
                            <CustomAvatar
                                alt='user-profile'
                                variant='rounded'
                                src={url}
                                size={120}
                                sx={{ fontSize: '40px' }}
                            />
                        ) : (
                            <CustomAvatar
                                alt='user-profile'
                                variant='rounded'
                                size={120}
                                sx={{ fontSize: '40px' }}
                            >
                                {getNumberInitials(detail?.fullname ?? '', 2)}
                            </CustomAvatar>
                        )}
                    </Box>
                </Grid2>
                <Grid2
                    container
                    spacing={5}
                    xs
                    sx={{
                        flexGrow: 1,
                    }}
                >
                    {info.map(({ id, title, value }) => (
                        <DisplayField
                            key={id}
                            label={t(title)}
                            value={value || DEFAULT_DISPLAY}
                            xs={12}
                            md={6}
                            lg={4}
                        />
                    ))}
                    <DisplayField
                        xs={12}
                        md={6}
                        lg={4}
                        key={5}
                        label={
                            <TextAction
                                value={task?.result?.totalTaskDone as any}
                                copy
                            >
                                {t('Task hoàn thành')}
                            </TextAction>
                        }
                        value={task?.result?.totalTaskDone || 0}
                    />
                    <DisplayField
                        xs={12}
                        md={6}
                        lg={4}
                        key={6}
                        label={
                            <TextAction value={avg} copy>
                                {t('Mức đánh giá')}
                            </TextAction>
                        }
                        value={
                            avg ? (
                                <EntityDetailButton
                                    text={avg}
                                    identifier={entityDetailIdentifier}
                                    entityId={detail?.id}
                                    type='RATING'
                                />
                            ) : (
                                0
                            )
                        }
                        onClick={() => modalRatingRef.current?.open()}
                    />
                </Grid2>
            </Grid2>
            <Grid2 sx={{ flexGrow: 1 }}>
                <BusinessTripFormTable assigneeId={detail.id} />
            </Grid2>
        </EntityDetailContainer>
    );
};

export default AssigneeInfo;
